import React from 'react';
import { InputLabel, FormControl, Select, MenuItem, Typography } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import TextField from '../../components/Textfields/TextField';
import PasswordField from '../../components/Textfields/PasswordField';
import Button from '../../components/Buttons/Button';
import LinkButton from '../../components/Buttons/LinkButton';
import RadioButtonGroup from '../../components/Buttons/RadioButtonGroup';
import Text from '../../components/Typography/Text';
import FormMessage from '../../components/Messages/FormMessage';
import { validateEmail } from '../../lib/validate/validate';
import {
  loginWeb,
  removeLoginMessage,
  getLogoutMessage,
} from '../../services/AuthService';
import LogoutMessageModal from '../../components/Modals/LogoutMessageModal';
import ExternalTemplate from '../../components/Templates/ExternalTemplate';
import { useAppContext } from '../../context/AppContext';

export default function Login({ history }) {
  const { isOnboarding } = useAppContext();
  const [data, setData] = React.useState({
    email: '',
    password: '',
    correspondent: '',
    authenticationMode: 'Email',
  });

  const [logoutMessage, setLogoutMessage] = React.useState(getLogoutMessage());

  const [message, setMessage] = React.useState({
    type: '',
    value: '',
  });

  const [loading, setLoading] = React.useState(false);
  const [correspondents, setCorrespondents] = React.useState([]);

  window.onbeforeunload = () => {
    removeLoginMessage();
  };

  const handleChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    setData({ ...data, [input.name]: input.value });
  };

  const validate = async () => {
    if (!data.email) {
      setMessage({ type: 'error', value: 'Email address is required.' });
      return;
    }
    if (data.email) {
      if (validateEmail(data.email)) {
        setMessage({ type: 'error', value: 'Invalid email address.' });
        return;
      }
    }
    if (!data.password) {
      setMessage({ type: 'error', value: 'Password is required.' });
      return;
    }

    return true;
  };

  const handleLogin = async () => {
    setMessage({ type: '', value: '' });
    const isValid = await validate();

    if (!isValid) {
      return;
    }

    setLoading(true);
    try {
      const { sessionKey, correspondentsList } = await loginWeb(data);

      if (correspondentsList?.length > 1) {
        setMessage({
          type: 'success',
          value:
            'The email you entered have multiple accounts. Please select a correspondent.',
        });
        setCorrespondents(correspondentsList);
        setLoading(false);
      } else {
        setCorrespondents([]);
        history.push('/login-auth-code', { ...data, sessionKey });
      }
    } catch (error) {
      setCorrespondents([]);
      setMessage({ type: 'error', value: error.message });
      setLoading(false);
      return;
    }
  };

  return (
    <ExternalTemplate>
      <form autoComplete="off">
        <Text mb={40} variant="h2" label="Login Account" />
        {message.value && <FormMessage type={message.type} message={message.value} />}
        <TextField
          autoFocus={true}
          name="email"
          label="Email Address"
          placeholder="youremail@website.com"
          type="text"
          max={50}
          showIcon={true}
          value={data.email}
          onChange={handleChange}
        />
        <PasswordField
          mt={true}
          name="password"
          label="Password"
          placeholder="Password"
          type="password"
          max={20}
          showIcon={true}
          value={data.password}
          onChange={handleChange}
        />
        {correspondents?.length > 1 && (
          <FormControl fullWidth style={{ marginTop: 20 }}>
            <InputLabel shrink id="correspondent">
              Correspondent
            </InputLabel>
            <Select
              name="correspondent"
              fullWidth
              value={data.correspondent}
              onChange={handleChange}
            >
              {correspondents.map((correspondent) => {
                return (
                  <MenuItem key={correspondent} value={correspondent}>
                    {correspondent}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}
        <div className="grd-row nm">
          <div className="grd-cell">
            <Typography
              id="authenticationlabel"
              style={{
                fontWeight: '600',
                marginTop: '16px',
              }}
            >
              Send authentication code via:
            </Typography>
          </div>
          <div className="grd-cell-none">
            <LinkButton
              mt={16}
              label="Forgot Password"
              onClick={() => {
                history.push('/forgot-password');
              }}
            />
          </div>
        </div>
        <div className="grd-row nm">
          <div className="grd-cell">
            <RadioButtonGroup
              name="authenticationMode"
              value={data.authenticationMode}
              onChange={handleChange}
              options={['Text', 'Email', 'Authenticator']}
              style={{ display: 'inline' }}
            />
          </div>
        </div>
        <Button
          mt={true}
          disabled={loading}
          loading={loading}
          label={loading ? 'Verifying your account...' : 'Login'}
          onClick={handleLogin}
        />

        {isOnboarding && (
          <div>
            <div className="text-divider">
              <Text variant="subtitle2" label="Don't have an account?" />
              <Divider />
            </div>

            <Button
              label="Create an Account"
              onClick={() => {
                history.push('/register');
              }}
            />
          </div>
        )}
      </form>

      {!!logoutMessage && (
        <LogoutMessageModal
          open={!!logoutMessage}
          message={logoutMessage}
          close={() => {
            removeLoginMessage();
            setLogoutMessage('');
          }}
        />
      )}
    </ExternalTemplate>
  );
}
